import { ROUTES } from "@/constants/Routes"
import { Box, Link, Typography } from "@mui/material"
import Accordion from "@mui/material/Accordion"
import AccordionSummary from "@mui/material/AccordionSummary"
import AccordionDetails from "@mui/material/AccordionDetails"
import { ExpandMoreRounded } from "@mui/icons-material"
import { blueGrey } from "@mui/material/colors"
import { useUserStore } from "@/store/useUserStore"
import { useState } from "react"
import { useUserOrganizationStore } from "@/store/useUserOrganizationStore"

export function AccessManagement() {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const userId = useUserStore((s) => s.id)
  const isAdmin = useUserStore((s) => s.isAdmin)
  const isResponsible = useUserStore((s) => s.isResponsible)

  const organizationId = useUserOrganizationStore(
    (store) => store.organization?.id,
  )

  return (
    <>
      <Typography variant="body2">
        O acesso à plataforma se dá através da inserção de um e-mail{" "}
        <span style={{ fontWeight: 600 }}>válido</span> e de uma senha. Para
        fazer login basta clicar no botão "Login" no cabeçalho da página ou{" "}
        <Link href={ROUTES.AUTH.LOGIN}>acesse</Link> diretamente a página.
      </Typography>

      <Box
        sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%" }}
      >
        <Accordion
          expanded={expandedIndex === 0}
          onChange={() =>
            setExpandedIndex((prevIndex) => (prevIndex !== 0 ? 0 : null))
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRounded />}
            aria-controls="access-gain-content"
            id="access-gain-header"
          >
            <Typography variant="body2" color={blueGrey[700]} fontWeight={600}>
              Como obter acesso?
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Typography variant="body2">
              Somente duas categorias de usuários podem acessar a plataforma:
              usuários RAIP e usuários relacionados às organizações.
            </Typography>
            <Typography variant="body2">
              Usuários RAIP são cadastrados na plataforma por um administrador
              RAIP.
            </Typography>
            <Typography variant="body2">
              Usuários relacionados às organizações são cadastrados na
              plataforma através do cadastro da organização a qual pertencem.
              Cada usuário informado no campo pessoal na página de{" "}
              <Link href={ROUTES.AUTH.NEW_ACCOUNT}>
                registro de organização
              </Link>{" "}
              recebe um email de primeiro acesso.
            </Typography>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expandedIndex === 1}
          onChange={() =>
            setExpandedIndex((prevIndex) => (prevIndex !== 1 ? 1 : null))
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRounded />}
            aria-controls="access-recover-content"
            id="access-recover-header"
          >
            <Typography variant="body2" color={blueGrey[700]} fontWeight={600}>
              Como recupero o acesso?
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Typography variant="body2">
              Existem duas formas de recuperar a sua senha caso tenha esquecido
              ou perdido:
            </Typography>

            <ul
              style={{
                margin: 0,
                paddingLeft: "1.5rem",
                paddingTop: "0.25rem",
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <li>
                <Typography variant="body2">
                  Você pode utilizar a página{" "}
                  <Link href={ROUTES.AUTH.FORGOT_PASSWORD}>
                    Esqueci minha senha
                  </Link>{" "}
                  informando o seu e-mail para receber um link de redefinição de
                  senha.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Caso você seja um usuário RAIP pode solicitar a um(a)
                  administrador(a) RAIP para envio do link de redefinição de
                  senha para o e-mail utilizado no cadastro.
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Caso você seja um usuário de uma organização pode solicitar
                  ao(s) responsável(s) de sua organização para reenvio do email
                  com link de redefinição de senha.
                </Typography>
              </li>
            </ul>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expandedIndex === 2}
          onChange={() =>
            setExpandedIndex((prevIndex) => (prevIndex !== 2 ? 2 : null))
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRounded />}
            aria-controls="access-regain-content"
            id="access-regain-header"
          >
            <Typography variant="body2" color={blueGrey[700]} fontWeight={600}>
              Estou sem acesso. O que posso fazer?
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Typography variant="body2">
              Existem algumas razões pelas quais você pode estar sem acesso ao
              sistema:
            </Typography>

            <ul
              style={{
                margin: 0,
                paddingLeft: "1.5rem",
                paddingTop: "0.25rem",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <li>
                <Typography variant="body2">
                  Seu usuário foi excluído da plataforma
                </Typography>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "1.5rem",
                    paddingTop: "0.25rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <li>
                    <Typography variant="body2">
                      Solicite a criação de um novo usuário para um
                      administrador RAIP ou responsável de sua organização.
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="body2">
                  Você consegue acessar a plataforma, porém é sempre
                  redirecionado para a página de ativação de conta
                </Typography>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "1.5rem",
                    paddingTop: "0.25rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <li>
                    <Typography variant="body2">
                      Nesse caso, a sua conta ainda não é considerada como{" "}
                      <span style={{ fontWeight: 600 }}>"ativa"</span>, pois a
                      sua conta não teve a senha redefinida. Redefina a senha
                      conforme nossas políticas.
                    </Typography>
                  </li>
                </ul>
              </li>
              <li>
                <Typography variant="body2">
                  Meu usuário existe mas não consigo fazer login
                </Typography>
                <ul
                  style={{
                    margin: 0,
                    paddingLeft: "1.5rem",
                    paddingTop: "0.25rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                  }}
                >
                  <li>
                    <Typography variant="body2">
                      Seu acesso foi desativado pelo administrador RAIP ou
                      responsável de sua organização. Nesse caso entre em
                      contato para saber o motivo e solicite a reativação do
                      acesso.
                    </Typography>
                  </li>
                </ul>
              </li>
            </ul>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expandedIndex === 3}
          onChange={() =>
            setExpandedIndex((prevIndex) => (prevIndex !== 3 ? 3 : null))
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRounded />}
            aria-controls="access-revoke-content"
            id="access-revoke-header"
          >
            <Typography variant="body2" color={blueGrey[700]} fontWeight={600}>
              Como revogar um acesso?
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Typography variant="body2">
              Somente administradores RAIP e responsáveis pelas suas
              organizações podem revogar acessos de outros usuários à
              plataforma.
            </Typography>

            <ul
              style={{
                margin: 0,
                paddingLeft: "1.5rem",
                paddingTop: "0.25rem",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <li>
                <Typography variant="body2">
                  Administradores podem revogar somente o acesso de outros
                  usuários RAIP na página de{" "}
                  {userId && isAdmin ? (
                    <Link href={ROUTES.USER.EDIT}>edição de usuários</Link>
                  ) : (
                    "edição de usuários"
                  )}
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Responsáveis de organizações podem revogar o acesso de
                  usuários vinculados a sua organização na página de{" "}
                  {userId && isResponsible && organizationId ? (
                    <Link href={`${ROUTES.USER.EDIT}?userId=${userId}`}>
                      perfil da organização
                    </Link>
                  ) : (
                    "perfil da organização"
                  )}
                </Typography>
              </li>
            </ul>
          </AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expandedIndex === 4}
          onChange={() =>
            setExpandedIndex((prevIndex) => (prevIndex !== 4 ? 4 : null))
          }
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRounded />}
            aria-controls="access-reactivate-content"
            id="access-reactivate-header"
          >
            <Typography variant="body2" color={blueGrey[700]} fontWeight={600}>
              Como reativar um acesso?
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={{ display: "flex", flexDirection: "column", gap: 1 }}
          >
            <Typography variant="body2">
              De forma semelhante como a revogação de acessos de usuários,
              somente administradores RAIP e responsáveis por organizações podem
              reativar os acessos.
            </Typography>

            <ul
              style={{
                margin: 0,
                paddingLeft: "1.5rem",
                paddingTop: "0.25rem",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <li>
                <Typography variant="body2">
                  Administradores podem reativar os acessos de outros usuários
                  RAIP na página de{" "}
                  {userId && isAdmin ? (
                    <Link href={ROUTES.USER.EDIT}>edição de usuários</Link>
                  ) : (
                    "edição de usuários"
                  )}
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  Responsáveis de organizações podem reativar acessos de
                  usuários vinculados a sua organização na página de{" "}
                  {userId && isResponsible && organizationId ? (
                    <Link href={`${ROUTES.USER.EDIT}?userId=${userId}`}>
                      perfil da organização
                    </Link>
                  ) : (
                    "perfil da organização"
                  )}
                </Typography>
              </li>
            </ul>
          </AccordionDetails>
        </Accordion>
      </Box>
    </>
  )
}
